import React from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import isURL from 'validator/lib/isURL';
import { Icons } from '@monorepo/icons';
import { FormInput } from '@monorepo/react-components';
import {
  AffiliateSocialMediaPlatform,
  AffiliateSocialMediaProfile,
  IconsNames,
} from '@monorepo/types';

interface Props {
  form: UseFormReturn<any>;
  index: number;
  length: number;
  remove: () => void;
  add: () => void;
}

export const SocialMediaInput = ({
  form,
  index,
  length,
  remove,
  add,
}: Props) => {
  const maxInputs = 5;

  const name = `socialMediaLinks[${index}].link`;

  const { t } = useTranslation(['affiliate']);

  const link = form.watch(name) || '';

  const getAction = () => {
    if (index === maxInputs - 1) {
      return null;
    } else if (index === length - 1) {
      return <Icons iconName={IconsNames.Plus} onClick={add} />;
    }

    return <Icons iconName={IconsNames.Trash} onClick={remove} />;
  };

  return (
    <FormInput
      form={form}
      controllerProps={{
        name,
        rules: {
          validate: (value, { socialMediaLinks }) => {
            const filteredLinks = socialMediaLinks
              .map(({ link }: { link: string }) => link)
              .filter(Boolean);

            if (filteredLinks.length === 0) {
              return t(
                'affiliate:register_modal.form.errors.at_least_one_link',
              );
            }

            if (
              value &&
              !isURL(value, {
                protocols: ['http', 'https'],
                require_protocol: true,
              })
            ) {
              return t('affiliate:register_modal.form.errors.invalid_link');
            }

            const uniqueLinks = new Set(filteredLinks);

            if (filteredLinks.length !== uniqueLinks.size) {
              return t('affiliate:register_modal.form.errors.unique_links');
            }

            return true;
          },
        },
      }}
      props={{
        placeholder: t(
          'affiliate:register_modal.form.social_media_placeholder',
        ),
        addonBefore: <Icons iconName={getSocialMediaFromLink(link)} />,
        addonAfter: getAction(),
        size: 'middle',
      }}
    />
  );
};

export const getSocialMediaFromLink = (
  link: string,
): AffiliateSocialMediaProfile['platform'] => {
  return (
    Object.values(AffiliateSocialMediaPlatform).find((platform) =>
      link.includes(platform + '.'),
    ) || IconsNames.Link
  );
};
